import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import { SUPPORT_EMAIL, type FaqArticle } from '../data/faqCatalog';
import { resolveFaqScreenshot } from '../lib/resolveScreenshot';
import { FaqScreenshot } from './FaqScreenshot';
import styles from './FaqArticleItem.module.css';

interface FaqArticleItemProps {
  article: FaqArticle;
  isExpanded: boolean;
  onToggle: (articleId: string) => void;
  /** Ставится ТОЛЬКО на статью, открытую по внутреннему deep-link'у (AC-7). */
  itemRef?: RefObject<HTMLDivElement>;
}

// PARA-133 (AD-7, AC-3, AC-4, AC-10) — аккордеон одной статьи.
//
// Нативная <button aria-expanded aria-controls> + панель, связанная с ней по id.
// <details>/<summary> запрещён (AD-7): его состояние неуправляемо и конфликтует
// с deep-link'ом. Внешних UI-библиотек нет — ни одной новой зависимости.
//
// Свёрнутая панель НЕ рендерится вовсе: лениво грузящееся изображение внутри
// неё иначе запрашивалось бы сразу при открытии экрана — у всех статей каталога
// (их 21) без единого действия пользователя.
export function FaqArticleItem({
  article,
  isExpanded,
  onToggle,
  itemRef,
}: FaqArticleItemProps): JSX.Element {
  const { t, i18n } = useTranslation('faq');

  const buttonId = `faq-q-${article.id}`;
  const panelId = `faq-a-${article.id}`;
  // Кадр берётся под ТЕКУЩИЙ язык интерфейса: смена локали меняет и текст, и
  // изображение, не перезагружая экран (AC-5, EC-3).
  const screenshot = resolveFaqScreenshot(article.screenshotId, i18n.language);

  return (
    <div className={styles.item} ref={itemRef}>
      <h3 className={styles.heading}>
        <Button
          variant="ghost"
          fullWidth
          id={buttonId}
          className={styles.question}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={() => onToggle(article.id)}
        >
          <span className={styles.questionText}>{t(`articles.${article.id}.question`)}</span>
          <span className={isExpanded ? styles.markerExpanded : styles.marker} aria-hidden="true" />
        </Button>
      </h3>

      {isExpanded && (
        <div id={panelId} role="region" aria-labelledby={buttonId} className={styles.panel}>
          {/* Адрес поддержки приходит ИНТЕРПОЛЯЦИЕЙ из единственной константы —
              в словарях его нет ни в одной локали (Р-5, AC-19). */}
          <p className={styles.answer}>
            {t(`articles.${article.id}.answer`, { email: SUPPORT_EMAIL })}
          </p>
          {screenshot !== null && (
            // key по src: смена локали подменяет файл, и состояние «кадр не
            // загрузился» от прежней локали не должно на него переноситься.
            <FaqScreenshot
              key={screenshot}
              src={screenshot}
              alt={t(`articles.${article.id}.imageAlt`)}
            />
          )}
        </div>
      )}
    </div>
  );
}
